// Aba "Entregáveis" (27/10/2026) — substitui o Controle de Entregas antigo.
// Projetos em ordem cronológica com os entregáveis; saldo da OPERAÇÃO × saldo em
// aberto no FINANCEIRO; entregar, editar, corrigir a data da entrega (é ela que
// põe a entrega no mês do resultado); contrato novo entra sozinho; salvar o
// baseline e o ajuste do mês. As entregas daqui são as que alimentam o Painel.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App as AntApp, Button, Card, Col, DatePicker, Dropdown, Empty, Flex, Input, Modal, Row, Segmented, Spin, Statistic, Switch, Table, Tag, Tooltip, Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { usePerms } from '../../hooks/usePerms';
import * as api from './api';
import type { ItemCarteira, ProjetoCarteira } from './api';
import { ModalEntregar, ModalItem } from './AbaMes';
import { brl, dataBr, nomeDe, rotuloCompetencia } from './regras';

const { Text } = Typography;
const PERM_EDITAR = 'operacional.entregas_mensais.editar';
type Filtro = 'a_entregar' | 'entregues' | 'todos';

const TAG_ALERTA: Record<string, { cor: string; texto: string; dica: string }> = {
  entregue_nao_faturado: { cor: 'gold', texto: 'entregue, não faturado', dica: 'A operação entregou; a parcela segue em aberto no financeiro.' },
  faturado_nao_entregue: { cor: 'purple', texto: 'faturado, não entregue', dica: 'O financeiro já faturou; a operação ainda não entregou.' },
};

export default function AbaEntregaveis() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode(PERM_EDITAR);
  const [dados, setDados] = useState<api.Carteira | null>(null);
  const [fotos, setFotos] = useState<api.FotografiaSalva[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('a_entregar');
  const [mesEntrega, setMesEntrega] = useState<Dayjs | null>(null);
  const [busca, setBusca] = useState('');
  const [soDiferenca, setSoDiferenca] = useState(false);
  const [ocultarSemSaldo, setOcultarSemSaldo] = useState(false);
  const [editando, setEditando] = useState<{ item: any; contrato: any } | null>(null);
  const [entregando, setEntregando] = useState<ItemCarteira | null>(null);
  const [datasDe, setDatasDe] = useState<ItemCarteira | null>(null);

  const carregar = useCallback(async (incluirNovos = false) => {
    setCarregando(true);
    try {
      // Contrato novo entra sozinho: ao abrir, quem edita inclui os novos (idempotente).
      if (incluirNovos && podeEditar) {
        const r = await api.incluirContratosNovos();
        if (r.incluidos.length) message.info(`Contratos novos incluídos no controle: ${r.incluidos.join(', ')}.`);
        if (r.parcelas_novas) message.info(`${r.parcelas_novas} parcela(s) nova(s) de contratos novos viraram entregáveis.`);
      }
      const [c, f] = await Promise.all([api.obterCarteira(), api.obterFotografias(dayjs().year())]);
      setDados(c); setFotos(f.fotografias);
    } catch (e) { message.error(`Não carregou os entregáveis: ${api.erroDe(e)}`); }
    finally { setCarregando(false); }
  }, [message, podeEditar]);
  useEffect(() => { carregar(true); }, [carregar]);

  const competencia = (dados?.hoje || dayjs().format('YYYY-MM-DD')).slice(0, 7);
  const fotoViva = (tipo: 'baseline' | 'ajuste') => fotos.find(f => f.competencia.startsWith(competencia) && f.tipo === tipo && !f.substituida_em) || null;

  async function salvarPrevisao(tipo: 'baseline' | 'ajuste', sobrescrever = false) {
    try {
      const r = await api.fotografar(competencia, tipo, sobrescrever);
      message.success(`${tipo === 'baseline' ? 'Baseline' : 'Ajuste'} de ${rotuloCompetencia(`${competencia}-01`)} salvo: ${brl(r.total_provavel)} prováveis.`);
      await carregar();
    } catch (e) {
      if (api.jaExiste(e)) {
        modal.confirm({
          title: 'A previsão mensal já foi efetuada, deseja sobrescrever?', content: `${api.erroDe(e)} A versão anterior fica guardada em "Previsões salvas".`,
          okText: 'Sobrescrever', okButtonProps: { danger: true }, cancelText: 'Manter a atual', onOk: () => salvarPrevisao(tipo, true),
        });
      } else message.error(api.erroDe(e));
    }
  }

  // Filtro dos entregáveis; o projeto aparece se sobrar algum entregável (ou, sem filtro, se não tiver nenhum).
  const filtraItem = useCallback((i: ItemCarteira) => {
    if (i.status === 'cancelado') return false;
    if (filtro === 'a_entregar') return i.status === 'pendente';
    if (filtro === 'entregues') {
      if (!i.teve_entrega) return false;
      if (!mesEntrega) return true;
      const m = mesEntrega.format('YYYY-MM');
      return i.lancamentos.some(l => l.origem !== 'abertura' && l.data.startsWith(m));
    }
    return true;
  }, [filtro, mesEntrega]);

  const projetos = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return (dados?.projetos || [])
      .filter(p => !t || `${p.numero} ${p.titulo} ${p.responsavel || ''}`.toLowerCase().includes(t))
      .filter(p => !soDiferenca || Math.abs(p.diferenca) > 1 || p.entregue_nao_faturado > 0 || p.faturado_nao_entregue > 0)
      .filter(p => !ocultarSemSaldo || p.saldo_operacao > 0.005)
      .map(p => ({ ...p, visiveis: p.itens.filter(filtraItem) }))
      .filter(p => p.visiveis.length > 0 || (filtro !== 'entregues' && !p.itens.some(i => i.status !== 'cancelado')))
      // Ordem pelo código do projeto (Gustavo, 28/10/2026: "ordem alfabética pelo código").
      .sort((a, b) => String(a.numero).localeCompare(String(b.numero), 'pt-BR', { numeric: true }));
  }, [dados, busca, soDiferenca, ocultarSemSaldo, filtraItem, filtro]);

  const colunasItens = (p: ProjetoCarteira) => [
    {
      title: 'Entregável', render: (_: any, i: ItemCarteira) => (
        <span style={i.status === 'entregue' ? { color: '#2f7d4f' } : undefined}>
          {i.status === 'entregue' ? '✓ ' : ''}{i.descricao}
          {i.tipo === 'quantidade' && <Text type="secondary"> · {i.entregue_quantidade}/{i.quantidade_total} {i.unidade}</Text>}
          {i.antes_do_controle && <Tag style={{ marginLeft: 6 }}>entregue antes do controle</Tag>}
        </span>
      ),
    },
    { title: 'Valor', dataIndex: 'valor', align: 'right' as const, width: 110, render: (v: number) => brl(v, 2) },
    {
      title: 'Entregue', width: 170, render: (_: any, i: ItemCarteira) => {
        const ls = i.lancamentos.filter(l => l.origem !== 'abertura');
        if (!ls.length && !i.entregue_antes) return <Text type="secondary">—</Text>;
        return (
          <span>
            {i.entregue_antes > 0 && <div><Text type="secondary" style={{ fontSize: 12 }}>até 30/09: {brl(i.entregue_antes, 2)}</Text></div>}
            {ls.map(l => <div key={l.id} style={{ fontSize: 12 }}>{dataBr(l.data)} · {brl(l.valor, 2)}</div>)}
          </span>
        );
      },
    },
    { title: 'Saldo (operação)', dataIndex: 'saldo', align: 'right' as const, width: 120, render: (v: number) => (v > 0.005 ? <Text strong>{brl(v, 2)}</Text> : <Text type="secondary">—</Text>) },
    {
      title: 'Previsão', width: 130, render: (_: any, i: ItemCarteira) => i.status !== 'pendente' ? null
        : <span>{dataBr(i.data_prevista)}{i.probabilidade != null ? <><br /><Text type="secondary" style={{ fontSize: 12 }}>{i.probabilidade}%</Text></> : null}</span>,
    },
    {
      title: 'Financeiro (parcela)', width: 190, render: (_: any, i: ItemCarteira) => !i.parcela ? <Tag>sem parcela</Tag> : (
        <span>
          {i.parcela.numero != null && <Text type="secondary">{i.parcela.numero}{i.parcela.total ? `/${i.parcela.total}` : ''} · </Text>}{brl(i.parcela.valor, 2)}
          <br /><Tag color={i.parcela.em_aberto ? 'blue' : 'default'} style={{ marginRight: 0 }}>{i.parcela.em_aberto ? 'em aberto' : (i.parcela.nf ? `NF ${i.parcela.nf}` : i.parcela.status)}</Tag>
        </span>
      ),
    },
    { title: '', width: 150, render: (_: any, i: ItemCarteira) => i.alerta ? <Tooltip title={TAG_ALERTA[i.alerta].dica}><Tag color={TAG_ALERTA[i.alerta].cor} style={{ whiteSpace: 'normal' }}>{TAG_ALERTA[i.alerta].texto}</Tag></Tooltip> : null },
    {
      title: '', width: 110, render: (_: any, i: ItemCarteira) => {
        if (!podeEditar) return null;
        const ls = i.lancamentos.filter(l => l.origem !== 'abertura');
        const itens = [
          ...(i.status === 'pendente' ? [{ key: 'e', label: i.tipo === 'quantidade' ? 'Medir…' : 'Formalizar entrega…', onClick: () => setEntregando(i) }] : []),
          ...(ls.length ? [{ key: 'd', label: 'Datas das entregas…', onClick: () => setDatasDe(i) }] : []),
          ...(i.status !== 'cancelado' ? [{ key: 'ed', label: 'Editar entregável…', onClick: () => setEditando({ item: { ...i, contrato_id: p.contrato_id }, contrato: { numero: p.numero } }) }] : []),
        ];
        return <Dropdown trigger={['click']} menu={{ items: itens }}><Button size="small" type={i.status === 'pendente' ? 'primary' : 'default'}>Ações ▾</Button></Dropdown>;
      },
    },
  ];

  const totais = dados?.totais;
  const base = fotoViva('baseline'), ajuste = fotoViva('ajuste');
  return (
    <Spin spinning={carregando}>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Saldo a entregar (operação)" value={brl(totais?.saldo_operacao)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Em aberto no financeiro" value={brl(totais?.financeiro_aberto)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Entregue e não faturado" value={brl(totais?.entregue_nao_faturado)} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Faturado e não entregue" value={brl(totais?.faturado_nao_entregue)} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Flex gap={12} wrap align="center">
          <Text strong>Previsão de {rotuloCompetencia(`${competencia}-01`)}:</Text>
          <Text>Baseline {base ? <>{brl(base.total_provavel)} <Text type="secondary">(salvo em {dataBr(base.salvo_em)} por {nomeDe(base.salvo_por)})</Text></> : <Text type="secondary">não salvo</Text>}</Text>
          <Text>Ajuste {ajuste ? <>{brl(ajuste.total_provavel)} <Text type="secondary">(salvo em {dataBr(ajuste.salvo_em)} por {nomeDe(ajuste.salvo_por)})</Text></> : <Text type="secondary">não salvo</Text>}</Text>
          <div style={{ flex: 1 }} />
          {podeEditar && <Button type="primary" onClick={() => salvarPrevisao('baseline')}>Salvar baseline do mês</Button>}
          {podeEditar && <Button onClick={() => salvarPrevisao('ajuste')}>Salvar baseline ajustado</Button>}
        </Flex>
        <Text type="secondary" style={{ fontSize: 12 }}>A previsão usa a data prevista e a probabilidade de cada entregável em aberto (edite em Ações → Editar entregável). As versões salvas ficam na aba "Previsões salvas".</Text>
      </Card>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 12 }}>
        <Segmented value={filtro} onChange={v => setFiltro(v as Filtro)} options={[{ value: 'a_entregar', label: 'A entregar' }, { value: 'entregues', label: 'Entregues' }, { value: 'todos', label: 'Todos' }]} />
        {filtro === 'entregues' && <DatePicker picker="month" format="MMMM/YYYY" placeholder="Mês da entrega (todos)" value={mesEntrega} onChange={setMesEntrega} />}
        <Input.Search placeholder="Buscar projeto ou responsável" allowClear style={{ width: 260 }} onChange={e => setBusca(e.target.value)} />
        <Flex gap={6} align="center"><Switch size="small" checked={soDiferenca} onChange={setSoDiferenca} /><Text>Só com diferença operação × financeiro</Text></Flex>
        <Flex gap={6} align="center"><Switch size="small" checked={ocultarSemSaldo} onChange={setOcultarSemSaldo} /><Text>Ocultar projetos sem saldo da operação</Text></Flex>
        <div style={{ flex: 1 }} />
        <Button onClick={() => carregar(true)}>Recarregar</Button>
      </Flex>
      {/* Projeto numa cor, entregáveis noutra (Gustavo, 28/10/2026). */}
      <style>{`
        .em-carteira .em-linha-projeto > td { background: #e8f0fb !important; border-top: 1px solid #c9d8ef; }
        .em-carteira .em-linha-projeto:hover > td { background: #dce8f8 !important; }
        .em-carteira .ant-table-expanded-row > td { background: #fbf8ee !important; padding-left: 32px !important; }
        .em-itens .ant-table-thead > tr > th { background: #f3eedc !important; }
        .em-itens .ant-table-tbody > tr > td { background: #fffdf6 !important; }
        .em-itens .ant-table-tbody > tr:hover > td { background: #f8f2df !important; }
      `}</style>
      {projetos.length ? (
        <Table<ProjetoCarteira & { visiveis: ItemCarteira[] }> className="em-carteira" rowClassName={() => 'em-linha-projeto'} rowKey="contrato_id" size="small" pagination={false} dataSource={projetos} scroll={{ x: 1200 }}
          expandable={{
            expandedRowRender: p => p.visiveis.length
              ? <Table<ItemCarteira> className="em-itens" rowKey="id" size="small" pagination={false} dataSource={p.visiveis} columns={colunasItens(p) as any} scroll={{ x: 1100 }} />
              : <Empty description={p.novo ? 'Contrato novo ainda sem entregáveis (sem parcelas no financeiro). Use Projeto → Novo entregável.' : 'Sem entregáveis neste filtro.'} />,
          }}
          columns={[
            { title: 'Início', dataIndex: 'inicio', width: 100, render: v => dataBr(v) },
            {
              title: 'Projeto', render: (_, p) => (
                <span><Text strong>{p.numero}</Text> {p.titulo}{p.novo && <Tag color="green" style={{ marginLeft: 6 }}>novo</Tag>}
                  <br /><Text type="secondary" style={{ fontSize: 12 }}>{nomeDe(p.responsavel)}{p.ultima_entrega ? ` · última entrega ${dataBr(p.ultima_entrega)}` : ''}</Text></span>
              ),
            },
            { title: 'Valor', dataIndex: 'valor_total', align: 'right', render: v => brl(v) },
            { title: 'Entregue', dataIndex: 'entregue_total', align: 'right', render: v => brl(v) },
            { title: 'Saldo (operação)', dataIndex: 'saldo_operacao', align: 'right', render: v => <Text strong>{brl(v)}</Text> },
            { title: 'Em aberto (financeiro)', dataIndex: 'financeiro_aberto', align: 'right', render: v => brl(v) },
            {
              title: 'Diferença', dataIndex: 'diferenca', align: 'right', render: (v, p) => Math.abs(v) <= 1 ? <Text type="success">—</Text> : (
                <Tooltip title={`Operação ${brl(p.saldo_operacao)} × financeiro ${brl(p.financeiro_aberto)}. Entregue e não faturado: ${brl(p.entregue_nao_faturado)}; faturado e não entregue: ${brl(p.faturado_nao_entregue)}.`}>
                  <Text type="danger">{brl(v)}</Text>
                </Tooltip>
              ),
            },
            { title: 'A entregar', dataIndex: 'a_entregar', align: 'right', render: v => (v ? <Tag color="blue">{v}</Tag> : '—') },
            {
              title: '', render: (_, p) => podeEditar && (
                <Dropdown trigger={['click']} menu={{
                  items: [
                    { key: 'n', label: 'Novo entregável…', onClick: () => setEditando({ item: { contrato_id: p.contrato_id, tipo: 'valor' }, contrato: { numero: p.numero } }) },
                    { key: 'p', label: 'Puxar parcelas novas do financeiro', onClick: async () => {
                      try { const r = await api.puxarParcelas(p.contrato_id); message.success(`${r.criados} entregável(is) criado(s).`); await carregar(); }
                      catch (e) { message.error(api.erroDe(e)); }
                    } },
                    { key: 'r', danger: true, label: 'Retirar do controle…', onClick: () => modal.confirm({
                      title: `Retirar ${p.numero} do controle?`,
                      content: 'Apaga os entregáveis e as entregas deste projeto no controle (o financeiro e o contrato não mudam). Sem nada em aberto no financeiro, ele sai da lista.',
                      okText: 'Retirar', okButtonProps: { danger: true },
                      onOk: async () => {
                        try { const r = await api.removerDoControle(p.contrato_id); message.success(`${p.numero} retirado: ${r.itens} entregável(is) e ${r.lancamentos} lançamento(s).`); await carregar(); }
                        catch (e) { message.error(api.erroDe(e)); }
                      },
                    }) },
                  ],
                }}><Button size="small">Projeto ▾</Button></Dropdown>
              ),
            },
          ]} />
      ) : !carregando && <Empty description="Nenhum projeto neste filtro." />}

      {editando && <ModalItem estado={editando} onFechar={() => setEditando(null)} onSalvo={() => carregar()} />}
      {entregando && <ModalEntregar item={entregando as any} hoje={dados?.hoje || dayjs().format('YYYY-MM-DD')} onFechar={() => setEntregando(null)} onSalvo={() => carregar()} />}
      {datasDe && <ModalDatas item={datasDe} hoje={dados?.hoje || dayjs().format('YYYY-MM-DD')} inicio={dados?.inicio_controle || ''} onFechar={() => setDatasDe(null)} onMudou={() => carregar()} />}
    </Spin>
  );
}

// Corrigir a data (ou estornar) cada entrega lançada do entregável.
function ModalDatas({ item, hoje, inicio, onFechar, onMudou }: { item: ItemCarteira; hoje: string; inicio: string; onFechar: () => void; onMudou: () => void }) {
  const { message, modal } = AntApp.useApp();
  const ls = item.lancamentos.filter(l => l.origem !== 'abertura');
  const [datas, setDatas] = useState<Record<string, Dayjs>>(() => Object.fromEntries(ls.map(l => [l.id, dayjs(l.data)])));
  async function salvar(id: string) {
    try { await api.lancamentoData(id, datas[id].format('YYYY-MM-DD')); message.success('Data da entrega corrigida.'); onMudou(); }
    catch (e) { message.error(api.erroDe(e)); }
  }
  return (
    <Modal open title={`Datas das entregas — ${item.descricao}`} onCancel={onFechar} footer={null} destroyOnHidden width="94vw" style={{ maxWidth: 640, top: 16 }}>
      <Text type="secondary">A data define em que mês a entrega entra no resultado (Painel). Vale de {dataBr(inicio)} até hoje.</Text>
      <Table rowKey="id" size="small" pagination={false} dataSource={ls} style={{ marginTop: 8 }}
        columns={[
          { title: 'Valor', dataIndex: 'valor', align: 'right', render: v => brl(v, 2) },
          {
            title: 'Data', render: (_, l) => (
              <DatePicker format="DD/MM/YYYY" allowClear={false} value={datas[l.id]}
                disabledDate={d => d.isAfter(dayjs(hoje), 'day') || d.isBefore(dayjs(inicio), 'day')}
                onChange={d => d && setDatas(p => ({ ...p, [l.id]: d }))} />
            ),
          },
          {
            title: '', render: (_, l) => (
              <Flex gap={6}>
                <Button size="small" type="primary" disabled={datas[l.id]?.format('YYYY-MM-DD') === l.data} onClick={() => salvar(l.id)}>Salvar data</Button>
                <Button size="small" danger onClick={() => modal.confirm({
                  title: 'Estornar esta entrega?', content: 'O valor sai do realizado do mês e o entregável volta a pendente.',
                  onOk: async () => { try { await api.estornar(l.id); message.success('Entrega estornada.'); onMudou(); onFechar(); } catch (e) { message.error(api.erroDe(e)); } },
                })}>Estornar</Button>
              </Flex>
            ),
          },
        ]} />
    </Modal>
  );
}
